import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { TranslatePipe } from 'eshava-shared';

import { HelpService } from '@portal/core/help/help.service';
import { parseHelpText } from '@portal/core/help/help-markdown';
import { Icon } from '@portal/shared/ui/icon/icon';

/**
 * Slides in from the right and carries the explanation.
 *
 * **Not a modal**, and that is the point: no focus trap, no scroll lock, the rest of the page stays
 * operable. Help is read while filling something in -- a flyout that blocks input is a modal with a
 * different animation.
 *
 * On wide screens it pushes the content aside (the shell shrinks its content column), in the
 * compact class it covers it, because otherwise nothing of the form would be left.
 */
@Component({
  selector: 'portal-help-flyout',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [Icon, TranslatePipe],
  template: `
    @if (help.isOpen()) {
      <aside class="flyout" [attr.aria-label]="'help.title' | t">
        <header class="flyout__header">
          <h2 class="flyout__title">{{ 'help.title' | t }}</h2>
          <button type="button" class="flyout__close" [attr.aria-label]="'help.close' | t" (click)="help.close()">
            <portal-icon name="xmark" size="sm" />
          </button>
        </header>

        <div class="flyout__body">
          @for (block of blocks(); track $index) {
            @switch (block.kind) {
              @case ('paragraph') {
                <p>
                  @for (segment of block.segments; track $index) {
                    @switch (segment.kind) {
                      @case ('strong') {
                        <strong>{{ segment.value }}</strong>
                      }
                      @case ('code') {
                        <code>{{ segment.value }}</code>
                      }
                      @default {
                        {{ segment.value }}
                      }
                    }
                  }
                </p>
              }
              @case ('code') {
                <!-- The line breaks are the content here, which is why this is not a paragraph. -->
                <pre class="flyout__code">{{ block.value }}</pre>
              }
              @case ('list') {
                <ul>
                  @for (item of block.items; track $index) {
                    <li>
                      @for (segment of item; track $index) {
                        @switch (segment.kind) {
                          @case ('strong') {
                            <strong>{{ segment.value }}</strong>
                          }
                          @case ('code') {
                            <code>{{ segment.value }}</code>
                          }
                          @default {
                            {{ segment.value }}
                          }
                        }
                      }
                    </li>
                  }
                </ul>
              }
            }
          }
        </div>
      </aside>
    }
  `,
  styles: `
    /* Scrolls on its own rather than widening the flyout: the width is capped by the line-length
       rule, and a request body is longer than that. */
    .flyout__code {
      padding: var(--space-2) var(--space-3);
      overflow-x: auto;
      font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
      font-size: var(--text-xs);
      line-height: 1.5;
      color: var(--text);
      background-color: var(--surface-sunken);
      border: 1px solid var(--border);
      border-radius: var(--radius-sm);
    }

    .flyout {
      display: flex;
      flex-direction: column;
      width: var(--flyout-width);
      max-width: 100vw;
      height: 100%;
      background-color: var(--surface);
      border-left: 1px solid var(--border);
    }

    .flyout__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: var(--space-3);
      padding: var(--space-4);
      border-bottom: 1px solid var(--border);
    }

    .flyout__title {
      font-size: var(--text-base);
      color: var(--text-muted);
      text-transform: uppercase;
      letter-spacing: 0.06em;
    }

    .flyout__close {
      display: inline-flex;
      padding: var(--space-2);
      color: var(--text-muted);
      background: none;
      border: 0;
      border-radius: var(--radius-sm);
      cursor: pointer;
    }

    .flyout__close:hover {
      color: var(--text);
      background-color: var(--surface-raised);
    }

    .flyout__body {
      flex: 1;
      padding: var(--space-4);
      overflow-y: auto;
    }

    .flyout__body p,
    .flyout__body ul {
      /* The line-length rule applies here in particular: a help text is prose. */
      max-width: var(--content-max-text);
    }

    .flyout__body p + p,
    .flyout__body p + ul,
    .flyout__body ul + p {
      margin-top: var(--space-3);
    }

    .flyout__body ul {
      padding-left: var(--space-5);
      margin: 0;
    }

    .flyout__body li + li {
      margin-top: var(--space-2);
    }

    .flyout__body code {
      padding: 0.1em 0.35em;
      font-family: ui-monospace, monospace;
      font-size: 0.9em;
      background-color: var(--surface-sunken);
      border-radius: var(--radius-sm);
    }
  `
})
export class HelpFlyout {
  protected readonly help = inject(HelpService);

  protected readonly blocks = computed(() => {
    const text = this.help.text();
    return text === null ? [] : parseHelpText(text);
  });
}